import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DASHBOARD_ITEM, NAV_GROUPS } from '../../config/navItems';

const STORAGE_KEY = 'onetak.sidebar.collapsedGroups';

// 접힌 그룹 라벨 집합을 localStorage 에 저장한다. AdminLayout 이 화면마다 새로 그려져
// Sidebar 도 리마운트되므로, 펼침/접힘 상태를 유지하려면 로컬스토리지에 남겨야 한다.
function loadCollapsed() {
  try { return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')); } catch { return new Set(); }
}

// 접기/펼치기 표시용 셰브론(아이콘 세트에 별도 셰브론이 없어 인라인 SVG 로 둔다).
function Chevron({ open }) {
  return (
    <svg className={`sidebar-group-chevron${open ? ' open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

export default function Sidebar() {
  // 허용 메뉴 목록은 AuthContext 가 로그인 시점에 한 번 조회해 Sidebar/AdminLayout 이
  // 함께 사용한다(중복 조회 제거, 화면 단위 접근 제어와 항상 같은 기준을 보게 됨).
  const { allowedMenuUrls } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(loadCollapsed);
  const navRef = useRef(null);      // 스크롤 컨테이너(.sidebar-nav)
  const activeRef = useRef(null);   // 현재 선택된 메뉴 링크

  // 화면 이동 때마다 Sidebar 가 새로 그려지면서 스크롤이 맨 위로 초기화된다.
  // 선택된 메뉴가 사이드바(스크롤 영역)의 세로 중앙에 오도록 스크롤 위치를 맞춘다.
  useEffect(() => {
    const nav = navRef.current;
    const el = activeRef.current;
    if (!nav || !el) return;
    const navRect = nav.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const offsetWithinNav = (elRect.top - navRect.top) + nav.scrollTop;
    const target = offsetWithinNav - nav.clientHeight / 2 + el.clientHeight / 2;
    nav.scrollTop = Math.max(0, target);
  }, [location.pathname]);

  // null = 아직 로딩 중(권한 확인 전에는 아무 메뉴도 보여주지 않는다 - fail-safe)
  const loading = allowedMenuUrls === null;
  const canSee = (menuUrl) => allowedMenuUrls === 'ALL' || (allowedMenuUrls && allowedMenuUrls !== 'ALL' && allowedMenuUrls.has(menuUrl));

  const toggle = (label) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...next])); } catch { /* 무시 */ }
      return next;
    });
  };

  // 현재 경로가 속한 그룹인지(펼침 자동 유지에 사용)
  const isActivePath = (to) => location.pathname === to || location.pathname.startsWith(`${to}/`);

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        <img src="/onetak-icon.svg" alt="" className="sidebar-logo-icon" />
        one<span>tak</span>
      </div>
      <nav className="sidebar-nav" ref={navRef}>
        {!loading && (
          <>
            {/* 대시보드 - 최상단 단독 항목 */}
            <NavLink
              to={DASHBOARD_ITEM.to}
              ref={isActivePath(DASHBOARD_ITEM.to) ? activeRef : undefined}
              className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
            >
              <DASHBOARD_ITEM.icon />
              {DASHBOARD_ITEM.label}
            </NavLink>

            {/* 대분류 그룹 (2단 트리) */}
            {NAV_GROUPS.map((group) => {
              const items = group.items.filter((it) => canSee(it.menuUrl));
              if (items.length === 0) return null; // 볼 수 있는 자식이 없으면 그룹 숨김

              const hasActive = items.some((it) => isActivePath(it.to));
              // 사용자가 명시적으로 접었어도, 현재 위치한 그룹은 펼쳐서 위치를 보여준다.
              const expanded = hasActive || !collapsed.has(group.label);
              const GroupIcon = group.icon;

              return (
                <div className="sidebar-group" key={group.label}>
                  <button
                    type="button"
                    className="sidebar-group-header"
                    onClick={() => toggle(group.label)}
                    aria-expanded={expanded}
                  >
                    <GroupIcon />
                    <span className="sidebar-group-label">{group.label}</span>
                    <Chevron open={expanded} />
                  </button>
                  {expanded && (
                    <div className="sidebar-group-items">
                      {items.map(({ to, label }) => (
                        <NavLink
                          key={to}
                          to={to}
                          ref={isActivePath(to) ? activeRef : undefined}
                          className={({ isActive }) => `sidebar-link sidebar-link-sub${isActive ? ' active' : ''}`}
                        >
                          {label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </nav>
      <div className="sidebar-footer">onetak 관리자 v0.1</div>
    </aside>
  );
}
